import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    EVENT_TYPE_SOURCES,
    getPanelType,
    PANEL_FORM_MODEL,
    PANEL_TYPE,
    PANEL_TYPE_OPTIONS,
    PANEL_USER_TYPES,
    SOURCE_OPTION_MODEL
} from '../Model/Model';

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * Form of the Add Panel modal: panel type, user type filter and source. Without a panel it adds a new
 * one; with a panel it loads its current source and updates it.
 * Changing the type or the user type clears the source, because it belongs to the previous selection.
 * The user types and event types are fixed lists of the model; the zones, controllers and users are searched
 * with the simple search of their panel type.
 */
const AddPanelModalApp = ({ panel, onConfirm }) => {
    const { t } = useTranslation();
    const [item, setItem] = useState(() => PANEL_FORM_MODEL.fromPanel(panel));
    const [selectedSources, setSelectedSources] = useState(() => PANEL_FORM_MODEL.toSelectedSources(panel));
    const [emptyFields, setEmptyFields] = useState(false);

    const panelType = getPanelType(item.typeId);
    const isUserPanel = item.typeId === PANEL_TYPE.USER.ID;
    const isSearchPanel = !!panelType?.SEARCH;
    const userTypeId = item.userTypeId;
    // The users are searched by the selected user type, the zones and controllers do not filter by it //
    const userTypeIdsSearch = isUserPanel && userTypeId ? [Number(userTypeId)] : [];
    const sanitizeSearch = useMemo(() => SOURCE_OPTION_MODEL.toSearchOptions(panelType), [panelType]);

    const panelTypeOptions = useMemo(
        () => PANEL_TYPE_OPTIONS.map((option) => ({ id: option.id, name: t(option.name) })),
        [t]
    );

    const userTypeOptions = useMemo(
        () => PANEL_USER_TYPES.map((userType) => ({ id: userType.id, name: t(userType.name) })),
        [t]
    );

    const sourceOptions = useMemo(
        () => (item.typeId === PANEL_TYPE.EVENT.ID ? EVENT_TYPE_SOURCES : []).map((source) => SOURCE_OPTION_MODEL.toOption(source, t)),
        [item.typeId, t]
    );

    const handleChangeType = (e) => {
        setItem({ ...PANEL_FORM_MODEL.clear(), typeId: e.target.value });
        setSelectedSources([]);
        setEmptyFields(false);
    };

    const handleChangeUserType = (e) => {
        setItem((prev) => ({ ...prev, userTypeId: e.target.value, sourceId: null }));
        setSelectedSources([]);
    };

    const handleChangeSource = (sourceId) => setItem((prev) => ({ ...prev, sourceId }));

    /**
     * The chip fields allow one option, it is the source of the panel.
     */
    const handleChangeSources = (list) => {
        setSelectedSources(list ?? []);
        handleChangeSource(list?.[0]?.id ?? null);
    };

    const handleConfirm = () => {
        if (!PANEL_FORM_MODEL.isValidType(item) || !PANEL_FORM_MODEL.isValidUserType(item) || !PANEL_FORM_MODEL.isValidSource(item)) {
            setEmptyFields(true);
            return;
        }
        if (panel) onConfirm(panel.id, item);
        else onConfirm(item);
    };

    return {
        t,
        item,
        emptyFields,
        panelType,
        selectedSources,
        isUserPanel,
        isSearchPanel,
        userTypeId,
        userTypeIdsSearch,
        sanitizeSearch,
        panelTypeOptions,
        userTypeOptions,
        sourceOptions,
        handleChangeType,
        handleChangeUserType,
        handleChangeSources,
        handleConfirm
    };
};

export default AddPanelModalApp;
